export type ConversionVendor = 'google' | 'facebook';

export type ConversionEvent = {
  vendor: ConversionVendor;
  event: string;
  callback?: () => void;
};

/** Simplified event — vendor is resolved automatically from the registry */
export type TrackEventOptions = {
  event: string;
  callback?: () => void;
};

export type VendorTracker = {
  track: (event: ConversionEvent) => void;
};

export type ConversionTrackerRegistry = Partial<Record<ConversionVendor, VendorTracker>>;

export function useConversionTracker(registry: ConversionTrackerRegistry = {}) {
  /**
   * Fire one specific vendor. Useful if you need targeted tracking.
   */
  const trackConversionForVendor = (event: ConversionEvent) => {
    const tracker = registry[event.vendor];
    if (!tracker) {
      console.warn(`[ConversionTracker] No tracker registered for vendor "${event.vendor}"`);
      event.callback?.();
      return;
    }
    tracker.track(event);
  };

  /**
   * Fire all registered vendors for a logical event name.
   * The callback fires once after all vendors have been notified.
   */
  const trackConversion = ({ event, callback }: TrackEventOptions) => {
    const vendors = Object.keys(registry) as ConversionVendor[];
    if (vendors.length === 0) {
      console.warn('[ConversionTracker] No vendors registered');
      callback?.();
      return;
    }
    for (const vendor of vendors) {
      registry[vendor]?.track({ vendor, event, callback });
    }
  };

  return { trackConversion, trackConversionForVendor };
}
